/**
 * Utility functions for appointment validation and management
 */

/**
 * Checks if a user can join a virtual appointment based on appointment details and payment status
 * 
 * @param {Object} appointment - The appointment object
 * @param {string} appointment.event_type - The type of appointment (should be "VIRTUAL" for virtual appointments)
 * @param {Object} appointment.status_appointment - The appointment status object
 * @param {string} appointment.status_appointment.code - The status code (e.g., "CAPPT" for confirmed)
 * @param {string} appointment.status_appointment.description - The status description
 * @param {Array} appointment.services_orders - Array of service orders
 * @param {Object} appointment.services_orders[].service_type - Service type object containing price
 * @param {number} appointment.services_orders[].service_type.price - The price of the service
 * @param {string} paymentStatus - The payment status (e.g., "completed", "COMPLETED")
 * @param {Object} options - Additional options for flexible property mapping
 * @param {string} options.priceSource - Alternative price source (for backwards compatibility)
 * 
 * @returns {boolean} - Returns true if the user can join the virtual appointment, false otherwise
 */
export const canJoinVirtualAppointment = (appointment, paymentStatus, options = {}) => {
  // Validate required parameters
  if (!appointment) {
    return false;
  }

  // First check if appointment is virtual
  const isVirtual = appointment?.event_type === "VIRTUAL";
  if (!isVirtual) {
    return false;
  }
  
  // Check if appointment is confirmed (with fallback for different status formats)
  const isConfirmed = appointment?.status_appointment?.code === "CAPPT" || 
                     appointment?.status_appointment?.description?.toLowerCase() === "confirmed";
  if (!isConfirmed) {
    return false;
  }
  
  // Priority-based validation: free appointments first, then paid confirmed appointments
  const appointmentPrice = appointment?.services_orders?.[0]?.service_type?.price || 
                          options.priceSource || 0;
  const isFreeAppointment = parseFloat(appointmentPrice) === 0;
  
  // If appointment is free, allow join immediately (just need to be virtual and confirmed)
  if (isFreeAppointment) {
    return true;
  }
  
  // If appointment has a price, check payment status
  // Handle different payment status formats and property names
  const normalizedPaymentStatus = (paymentStatus || '').toString().toUpperCase();
  const isPaid = normalizedPaymentStatus === "COMPLETED";
  
  return isPaid;
};

/**
 * Alternative wrapper for props-based components (backwards compatibility)
 * 
 * @param {Object} props - Component props containing appointment details
 * @param {string} props.event_type - The type of appointment
 * @param {string} props.appt_status_code - The appointment status code
 * @param {string} props.order_status - The order/payment status
 * @param {number|string} props.price - The appointment price
 * @param {number|string} props.new_price - Alternative price field
 * 
 * @returns {boolean} - Returns true if the user can join the virtual appointment
 */
export const canJoinVirtualAppointmentFromProps = (props) => {
  if (!props) {
    return false;
  }

  // Transform props into appointment object format
  const appointment = {
    event_type: props.event_type,
    status_appointment: {
      code: props.appt_status_code,
      description: null // This version doesn't have description fallback from props
    },
    services_orders: [] // Will use priceSource option instead
  };

  // Get price with fallback logic
  const appointmentPrice = props.new_price != null ? props.new_price : props.price;
  
  // Use the main validation function with price source option
  return canJoinVirtualAppointment(
    appointment, 
    props.order_status, 
    { priceSource: appointmentPrice }
  );
};